import { fonts } from "@/theme/typography";
import { GlobalStyles, SxProps, Theme, Tooltip, Typography, TypographyProps } from "@mui/material";
import { FC, memo, MouseEvent, useCallback, useState } from "react";

const BASELINE_ELLIPSIS_FONT = "MasterDataBaselineEllipsis";

export const BaselineEllipsisFontFace = () => (
  <GlobalStyles
    styles={{
      "@font-face": {
        fontFamily: BASELINE_ELLIPSIS_FONT,
        src: 'local("Arial"), local("Helvetica"), local("Roboto"), local("Segoe UI")',
        unicodeRange: "U+2026",
      },
    }}
  />
);

type TEllipsisTextProps = {
  value?: string | null;
  emptyText: string;
  color?: TypographyProps["color"];
  sx?: SxProps<Theme>;
};

const BASE_SX = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontFamily: `"${BASELINE_ELLIPSIS_FONT}", ${fonts}`,
} as const;

const EllipsisTextBase: FC<TEllipsisTextProps> = ({ value, emptyText, color, sx }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback((event: MouseEvent<HTMLElement>) => {
    const element = event.currentTarget;
    setIsOverflowing(element.scrollWidth > element.clientWidth);
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const text = (
    <Typography
      component="p"
      variant="body14"
      color={color}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={sx ? [BASE_SX, ...(Array.isArray(sx) ? sx : [sx])] : BASE_SX}
    >
      {value || emptyText}
    </Typography>
  );

  if (!isHovered || !isOverflowing || !value) {
    return text;
  }

  return (
    <Tooltip title={value} placement="bottom-start" open disableHoverListener>
      {text}
    </Tooltip>
  );
};

export const EllipsisText = memo(EllipsisTextBase);
